<template>
  <section>
    <div class="document-wrapper">
      <DocumentIndex
        v-model="page"
        :page-titles="pageTitles"
      />

      <div>
        <DocumentPreviousButton v-model="page" />

        <DocumentCoverPage
          v-if="page === 0"
          :plan="plan"
        />

        <DocumentExecutiveSummaryPage
          v-if="page === 1"
          :plan="plan"
        />

        <DocumentIntroductionPage
          v-if="page === 2"
          :plan="plan"
        />

        <DocumentDiagnosticsPage
          v-if="page === 3"
          :plan="plan"
        />

        <template
          v-for="(section, index) in plan.vision"
          :key="index"
        >
          <DocumentVisionPage
            v-if="page === (4 + index)"
            :section="section"
          />
        </template>

        <DocumentCatalyticProjects
          v-if="page === (5 + plan.vision.length)"
          :frameworks="frameworks"
          :projects="projects"
          :selected-categories="selectedCategories"
          :selected-framework="selectedFramework"
        />

        <DocumentConclusionPage
          v-if="page === pageTitles.length - 1"
          :plan="plan"
        />

        <DocumentNextButton
          v-model="page"
          :pages="pageTitles.length"
        />
      </div>

      <div class="order-3 flex flex-col gap-3">
        <DocumentFilters
          v-model:selected-framework="selectedFramework"
          v-model:selected-categories="selectedCategories"
          :frameworks="frameworks"
        />

        <a
          class="btn"
          target="_blank"
          :href="$router.resolve({ name: 'app_plan_pdf_preview' }).href"
        >
          View Pdf
        </a>

        <AppButton
          class="w-full"
          :processing="pdfStore.status === 'Pending'"
          @click="downloadPdf"
        >
          {{ pdfStore.status === 'Pending' ? 'Generating Pdf' : 'Download Pdf' }}
        </AppButton>
      </div>
    </div>
  </section>

  <DocumentMobileIndex
    v-model:page="page"
    v-model:show-index="showIndex"
    :page-titles="pageTitles"
    :open="showIndex"
  />

  <DocumentMobileFilters
    v-model:selected-framework="selectedFramework"
    v-model:selected-categories="selectedCategories"
    :open="showFilters"
    :frameworks="frameworks"
  />

  <DocumentMobilePaginator
    v-model:page="page"
    v-model:show-index="showIndex"
    v-model:show-filters="showFilters"
    :pages="pageTitles.length"
  />
</template>

<script setup>
import { Network } from 'vis-network';
import { computed, ref, watch } from 'vue';

import {
  DocumentCoverPage,
  DocumentFilters,
  DocumentIndex,
  DocumentMobileFilters,
  DocumentMobileIndex,
  DocumentMobilePaginator,
  DocumentNextButton,
  DocumentPreviousButton,
  DocumentVisionPage,
} from '@/components/plan-document';

import {
  DocumentCatalyticProjects,
  DocumentConclusionPage,
  DocumentDiagnosticsPage,
  DocumentIntroductionPage,
  DocumentExecutiveSummaryPage,
} from '@/components/plan-document/pages';

import { NodeType } from '@/const';
import { AppButton } from '@/components/buttons/index.js';
import { useHttp } from '@/composables/index.js';
import { HttpStatusCode } from 'axios';
import { useRoute, useRouter } from 'vue-router';
import { usePdfStore } from '@/store/planPdf.js';

const route = useRoute();
const router = useRouter();

const props = defineProps({
  frameworks: {
    type: Array,
    default: () => [],
  },
  plan: {
    type: Object,
    default: () => ({}),
  },
  nodes: {
    type: Object,
    default: () => ({}),
  },
  edges: {
    type: Object,
    default: () => ({}),
  },
});

const page = ref(0);
const network = ref();
const selectedCategories = ref([]);
const selectedFramework = ref(null);
const showFilters = ref(false);
const showIndex = ref(false);

const projects = computed(() => {
  return props.nodes.get({ filter: node => node.type === NodeType.Project });
});

const pageTitles = computed(() => {
  const visionHeadings = props.plan.vision ? props.plan.vision.map(section => 'District Vision: ' + section.heading) : [];
  return ['Cover Page', 'Executive Summary', 'Introduction', 'Diagnostics', ...visionHeadings, 'Catalytic Projects', 'Conclusion'];
});

// Populates the selectedCategories array with all framework categories if there is not a selectedFramework
const populateSelectedCategories = () => {
  if (selectedFramework.value) {
    selectedCategories.value = selectedFramework.value.categories.map(category => category.id);
  } else {
    props.frameworks.forEach(framework => {
      selectedCategories.value.push(...framework.categories.map(category => category.id));
    });
  }
};

// Initialises vis js so methods for interacting with nodes can be accessed
const initializeNetwork = () => {
  const nodes = props.nodes;
  const edges = props.edges;
  const container = document.createElement('div'); // Temporary container
  const data = { nodes, edges };
  const options = {};

  network.value = new Network(container, data, options);
};

const pdfStore = usePdfStore();

const downloadPdf = () => {
  const pdfPreviewRoute = router.resolve({ name: 'app_plan_pdf_preview' }).href;

  pdfStore.startGeneration(
    route.params.plan,
    pdfPreviewRoute,
  );
};

watch(selectedFramework, () => {
  populateSelectedCategories();
}, { immediate: true });

initializeNetwork();

</script>
